const DEFAULT_DEBOUNCE_MS = 1000;

export const hasLocalMediaInHtml = (html: string) =>
  /(?:src|href)=["'](?:blob:|data:)/i.test(html);

export const snapshotOf = (html: string, name: string) =>
  `${name.trim()}\n${html}`;

export type BriefAutosavePersist = (payload: {
  formV2: string;
  campaignName?: string;
}) => void;

export type BriefAutosaveDeps = {
  getHtml: () => string;
  getName: () => string;
  persist: BriefAutosavePersist;
  debounceMs?: number;
};

export type BriefAutosave = {
  scheduleSave: () => void;
  saveNow: () => void;
  pause: () => void;
  resumeAndSave: () => void;
  resetSnapshot: (html: string, name: string) => void;
  clearPending: () => void;
  markSaveSucceeded: (html: string, name: string) => void;
  markSaveFailed: () => void;
  dispose: () => void;
};

export function createBriefAutosave(deps: BriefAutosaveDeps): BriefAutosave {
  const debounceMs = deps.debounceMs ?? DEFAULT_DEBOUNCE_MS;
  let lastSaved = "";
  let saving = false;
  let paused = false;
  let retryAfterSave = false;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const cancelTimer = () => {
    if (!timer) return;
    clearTimeout(timer);
    timer = null;
  };

  const saveNow = () => {
    if (paused) return;
    if (saving) {
      retryAfterSave = true;
      return;
    }

    const html = deps.getHtml();
    if (!html || hasLocalMediaInHtml(html)) return;

    const name = deps.getName().trim();
    if (snapshotOf(html, name) === lastSaved) return;

    saving = true;
    deps.persist({
      formV2: html,
      campaignName: name || undefined,
    });
  };

  const scheduleSave = () => {
    if (paused) return;
    if (saving) retryAfterSave = true;
    cancelTimer();
    timer = setTimeout(() => {
      timer = null;
      saveNow();
    }, debounceMs);
  };

  return {
    scheduleSave,
    saveNow,
    pause: () => {
      paused = true;
      cancelTimer();
    },
    resumeAndSave: () => {
      paused = false;
      cancelTimer();
      saveNow();
    },
    resetSnapshot: (html, name) => {
      lastSaved = snapshotOf(html, name);
      saving = false;
      retryAfterSave = false;
      cancelTimer();
    },
    clearPending: () => {
      lastSaved = "";
      saving = false;
      paused = false;
      retryAfterSave = false;
      cancelTimer();
    },
    markSaveSucceeded: (html, name) => {
      saving = false;
      lastSaved = snapshotOf(html, name);
      if (!retryAfterSave) return;
      retryAfterSave = false;
      scheduleSave();
    },
    markSaveFailed: () => {
      saving = false;
    },
    dispose: cancelTimer,
  };
}
